import React, { useEffect, useState } from "react"
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import Select from "react-select"
import { productsData } from "pages/admin/Products"
import FormLabel from "components/Extra/FormLabel"
import { FormInput } from "components/Extra/FormInput"
import { useTranslation } from "react-i18next"

interface AddNewCouponProps {
  toggle: () => void
  isOpen: boolean
}

export const AddNewCoupon: React.FC<AddNewCouponProps> = ({
  isOpen,
  toggle,
}) => {
  const { t } = useTranslation()

  const [openFirstAccordion, setOpenFirstAccordion] = useState("0")
  const [openSecondAccordion, setOpenSecondAccordion] = useState("0")
  const [selectedProduct, setselectedProduct] = useState<{
    value: string
    label: string
  } | null>(null)

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      couponsSerial: "",
      sku: "",
      couponProduct: null,
      productSize: "",
      price: 0,
      refillTimesPerWeek: 0,
      itemCountPerOnce: 0,
    },
    validationSchema: Yup.object({
      couponsSerial: Yup.string()
        .trim()
        .required(t("coupons_couponsSerialValidationMessage") as string),
      // sku: Yup.string().trim(),
      couponProduct: Yup.object({
        id: Yup.string().required(
          t("coupons_couponProductSerialValidationMessage") as string
        ),
        name: Yup.string().required(
          t("coupons_couponProductSerialValidationMessage") as string
        ),
      }),
      productSize: Yup.string()
        .trim()
        .required(t("coupons_productSizeValidationMessage") as string),
      price: Yup.number().required(
        t("coupons_priceValidationMessage") as string
      ),
      refillTimesPerWeek: Yup.number()
        .min(1)
        .max(7)
        .required(t("coupons_refillTimesPerWeekValidationMessage") as string),
      itemCountPerOnce: Yup.number().required(
        t("coupons_itemCountPerOnceValidationMessage") as string
      ),
    }),
    onSubmit: values => {
      const {
        couponProduct,
        couponsSerial,
        itemCountPerOnce,
        price,
        productSize,
        refillTimesPerWeek,
        sku,
      } = values

      console.log(values)

      // TODO: Dispatch
      validation.resetForm()
      toggle()
    },
  })

  useEffect(() => {
    validation.resetForm()
  }, [isOpen])

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle} tag="h3">
        <h4 className="text-capitalize fw-semibold font-size-24">
          {t("coupons_addNewCoupon")}
        </h4>
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={e => {
            e.preventDefault()
            validation.handleSubmit()
            return false
          }}
        >
          <Accordion open={openFirstAccordion}>
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenFirstAccordion(openFirstAccordion === "0" ? "1" : "0")
                }
              >
                {t("common_basicInfo")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <FormInput
                  label={t("coupons_couponSerial") as string}
                  name="couponsSerial"
                  type="text"
                  placeholder={t("coupons_enterCouponSerial") as string}
                  validation={validation}
                  required
                />
                <FormInput
                  label={t("coupons_sku") as string}
                  name="sku"
                  type="text"
                  placeholder={t("coupons_enterSku") as string}
                  validation={validation}
                />
                <FormGroup>
                  <FormLabel htmlFor="couponProduct" required>
                    {t("coupons_product")}
                  </FormLabel>
                  <Select
                    id="couponProduct"
                    value={selectedProduct}
                    onChange={(
                      selectedOption: { value: string; label: string } | null
                    ) => {
                      setselectedProduct(selectedOption)
                      validation.setFieldValue("couponProduct", {
                        id: selectedOption?.value,
                        name: selectedOption?.label,
                      })
                    }}
                    options={productsData.map(one => ({
                      value: one.id,
                      label: one.name,
                    }))}
                    className="select2-selection"
                  />
                  {validation.touched.couponProduct &&
                  validation.errors.couponProduct ? (
                    <FormFeedback type="invalid">
                      {validation.errors.couponProduct}
                    </FormFeedback>
                  ) : null}
                </FormGroup>
                <FormInput
                  label={t("coupons_productSize") as string}
                  name="productSize"
                  type="number"
                  placeholder={t("coupons_enterCouponSize") as string}
                  validation={validation}
                  info={t("coupons_couponSizeInfo") as string}
                  required
                />
                <FormInput
                  label={t("common_priceCurrency") as string}
                  name="price"
                  type="number"
                  placeholder={t("coupons_enterprice") as string}
                  validation={validation}
                  info={t("coupons_priceInfo") as string}
                  required
                />
              </AccordionBody>
            </AccordionItem>
          </Accordion>
          <Accordion open={openSecondAccordion} className="mt-4">
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenSecondAccordion(
                    openSecondAccordion === "0" ? "1" : "0"
                  )
                }
              >
                {t("coupons_waterRefillTimeing")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <FormInput
                  label={t("coupons_refillTimesPerWeek") as string}
                  name="refillTimesPerWeek"
                  type="number"
                  placeholder={t("coupons_enterRefillTimesPerWeek") as string}
                  validation={validation}
                  info={t("coupons_refillTimesPerWeekInfo") as string}
                  required
                />
                <FormInput
                  label={t("coupons_itemCountPerOnce") as string}
                  name="itemCountPerOnce"
                  type="number"
                  placeholder={t("coupons_enterItemCountPerOnce") as string}
                  validation={validation}
                  required
                />
              </AccordionBody>
            </AccordionItem>
          </Accordion>
          <Row>
            <Col>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="submit"
                  className={`btn bg-gradiant-blue text-white border-0 save-customer`}
                  disabled={!(validation.isValid && validation.dirty)}
                >
                  {t("common_create")}
                </button>
                <button
                  onClick={toggle}
                  type="button"
                  className="btn btn-cancel text-primary border border-cyan text-cyan"
                >
                  {t("common_cancel")}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  )
}
